import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FileCheck, GitCompareArrows, Minus, Plus, ShieldAlert, Bookmark, BellRing, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import api, { errMsg } from '../lib/api';
import ProductCard from '../components/ProductCard';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { gbp, catOf, STATUS_LABELS, setPageMeta, mdToHtml } from '../lib/shop';

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [coas, setCoas] = useState([]);
  const [articles, setArticles] = useState([]);
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState('research');
  const [imgIdx, setImgIdx] = useState(0);
  const [selVarId, setSelVarId] = useState('');
  const [interestEmail, setInterestEmail] = useState('');
  const { add, toggleCompare, inCompare } = useCart();
  const { user, refresh } = useAuth();

  useEffect(() => {
    setP(null); setImgIdx(0); setTab('research'); setSelVarId('');
    api.get(`/products/${slug}`).then((r) => {
      setP(r.data);
      setPageMeta(r.data.seo_title || r.data.name, r.data.seo_description || r.data.short_description);
      api.get(`/products/${slug}/related`).then((x) => setRelated(x.data)).catch(() => {});
      api.get('/coas', { params: { product_id: r.data.id } }).then((x) => setCoas(x.data)).catch(() => {});
      api.get('/journal').then((x) => setArticles(x.data)).catch(() => {});
    }).catch(() => setP(false));
  }, [slug]);

  const activeVariants = (p?.variants || []).filter((v) => v.active !== false);
  useEffect(() => {
    if (activeVariants.length && !selVarId) setSelVarId(activeVariants[0].id);
  }, [p?.id]);

  if (p === null) return <div className="max-w-7xl mx-auto px-4 py-20"><div className="aspect-video bg-[#F4F4F0] animate-pulse" /></div>;
  if (p === false) return <div className="max-w-7xl mx-auto px-4 py-20 text-center" data-testid="product-not-found"><p className="font-display font-bold uppercase text-2xl">Product not found</p></div>;

  const selVar = activeVariants.find((v) => v.id === selVarId) || null;
  const hasVariants = activeVariants.length > 0;
  const disp = selVar ? {
    price: selVar.retail_price, strength: selVar.strength, sku: selVar.sku || p.sku,
    batch: selVar.batch_number, coa: selVar.coa_available, status: selVar.status || 'available',
    stock: selVar.stock_quantity ?? 0,
    inStock: p.track_stock ? (selVar.stock_quantity || 0) > 0 : true,
  } : {
    price: p.effective_price ?? p.retail_price, strength: p.strength, sku: p.sku,
    batch: p.batch_number, coa: p.coa_available, status: p.status,
    stock: p.stock_quantity, inStock: p.in_stock,
  };

  const cat = catOf(p.category);
  const purchasable = disp.status === 'available' && disp.inStock && (!hasVariants || selVar);
  const wantsInterest = ['register_interest', 'coming_soon', 'enquiry_only', 'out_of_stock'].includes(disp.status)
    || (disp.status === 'available' && !disp.inStock);
  const images = (p.gallery_images?.length ? p.gallery_images : [p.primary_image]).filter(Boolean);
  const ro = p.research_overview;
  const relatedArticles = ro?.related?.length
    ? articles.filter((a) => ro.related.includes(a.slug))
    : articles.slice(0, 2);

  const saveProduct = async () => {
    if (!user) { toast.error('Sign in to save products'); return; }
    const saved = new Set(user.saved_products || []);
    saved.has(p.id) ? saved.delete(p.id) : saved.add(p.id);
    await api.put('/auth/profile', { name: user.name, addresses: user.addresses || [], saved_products: [...saved], saved_articles: user.saved_articles || [] });
    await refresh();
    toast.success(saved.has(p.id) ? 'Saved to your account' : 'Removed from saved products');
  };

  const submitInterest = async () => {
    if (!/^\S+@\S+\.\S+$/.test(interestEmail)) { toast.error('Enter a valid email'); return; }
    try {
      await api.post('/interest', { product_id: p.id, email: interestEmail });
      toast.success('Interest registered — we will be in touch.');
      setInterestEmail('');
    } catch (e) { toast.error(errMsg(e)); }
  };

  const TABS = [
    ['research', 'Research Information'], ['spec', 'Specification'], ['storage', 'Storage'],
    ['documents', 'Documents'], ['shipping', 'Shipping'], ['important', 'Important'],
  ];

  const RO_SECTIONS = [
    ['overview', 'Overview'], ['research_context', 'Research Context'],
    ['mechanism', 'Mechanism / Area of Study'], ['published_research', 'Published Research'],
    ['documentation', 'Documentation'], ['important', 'Important Information'],
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="product-page">
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org', '@type': 'Product',
        name: `${p.name}${disp.strength ? ` ${disp.strength}` : ''}`,
        image: images, description: p.short_description, sku: disp.sku,
        offers: { '@type': 'Offer', priceCurrency: 'GBP', price: disp.price,
          availability: purchasable ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock' },
      })}</script>
      <nav className="font-mono2 text-[10px] uppercase tracking-[0.18em] text-[#8A8F95] mb-6" data-testid="breadcrumbs">
        <Link to="/" className="hover:text-[#0B0F12]">Home</Link> / <Link to="/products" className="hover:text-[#0B0F12]">Products</Link> /{' '}
        <Link to={`/products?category=${p.category}`} className="hover:text-[#0B0F12]" style={{ color: cat.color }}>{cat.name}</Link> / <span className="text-[#0B0F12]">{p.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div>
          <div className="border hairline bg-white aspect-square overflow-hidden">
            <img data-testid="product-image" src={selVar?.image || images[imgIdx]} alt={p.name} className="w-full h-full object-cover" />
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 mt-2">
              {images.map((im, i) => (
                <button key={i} data-testid={`gallery-thumb-${i}`} onClick={() => setImgIdx(i)}
                  className={`w-16 h-16 border ${i === imgIdx ? 'border-[#0B0F12]' : 'hairline'} overflow-hidden`}>
                  <img src={im} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="font-mono2 text-[11px] uppercase tracking-[0.2em]" style={{ color: cat.color }}>{cat.name}</p>
          <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl leading-[0.95] mt-2" data-testid="product-name">{p.name}</h1>
          {!hasVariants && <p className="font-mono2 text-sm uppercase tracking-wider text-[#575B60] mt-2" data-testid="product-variant">{[p.strength, p.format, p.variant].filter(Boolean).join(' · ')}</p>}
          {hasVariants && <p className="font-mono2 text-sm uppercase tracking-wider text-[#575B60] mt-2">{p.format} · {activeVariants.length} strengths available</p>}
          {p.alternate_name && <p className="text-sm text-[#8A8F95] mt-1">{p.alternate_name}</p>}

          {hasVariants && (
            <div className="mt-5">
              <label className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F95] block mb-2">Select strength</label>
              <div className="relative max-w-xs">
                <select data-testid="strength-select" value={selVarId} onChange={(e) => setSelVarId(e.target.value)}
                  className="w-full appearance-none border-2 border-[#0B0F12] bg-white px-4 py-3.5 font-mono2 text-sm font-semibold uppercase tracking-wider focus:outline-none focus:border-[#E54D2E] cursor-pointer">
                  {activeVariants.map((v) => {
                    const label = `${v.strength} — £${Number(v.retail_price).toFixed(2)}${p.track_stock && v.status === 'available' && (v.stock_quantity || 0) <= 0 ? ' (out of stock)' : ''}${v.status && v.status !== 'available' ? ` (${STATUS_LABELS[v.status]})` : ''}`;
                    return (
                      <option key={v.id} value={v.id} data-testid={`strength-option-${v.strength.replace(/[^a-z0-9]+/gi, '-')}`}>{label}</option>
                    );
                  })}
                </select>
                <ChevronDown className="w-4 h-4 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 mt-5">
            {disp.price > 0 ? (
              <span className="font-mono2 font-bold text-2xl" data-testid="product-price">{gbp(disp.price)}</span>
            ) : (
              <span className="font-mono2 font-bold text-2xl" data-testid="product-price">TBC</span>
            )}
            {!hasVariants && p.compare_at_price > disp.price && <span className="font-mono2 text-sm text-[#8A8F95] line-through">{gbp(p.compare_at_price)}</span>}
            {p.badge && <span className="bg-[#0B0F12] text-white font-mono2 text-[9px] uppercase tracking-widest px-2 py-1">{p.badge}</span>}
          </div>

          <div className="mt-4 flex flex-wrap gap-2 font-mono2 text-[10px] uppercase tracking-wider">
            <span data-testid="product-status" className="border hairline px-3 py-1.5 flex items-center gap-1.5 bg-white">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: purchasable ? '#10B981' : '#F59E0B' }} />
              {STATUS_LABELS[disp.status] || disp.status}{disp.status === 'available' ? (disp.inStock ? (p.track_stock ? ` · ${disp.stock} in stock` : ' · In stock') : ' · Out of stock') : ''}
            </span>
            {disp.batch && <span className="border hairline px-3 py-1.5 bg-white" data-testid="product-batch">Batch {disp.batch}</span>}
            {disp.coa && <span className="border hairline px-3 py-1.5 bg-white flex items-center gap-1" data-testid="product-coa-badge"><FileCheck className="w-3.5 h-3.5" /> COA Available</span>}
            <span className="border hairline px-3 py-1.5 bg-white" data-testid="product-sku">SKU {disp.sku}</span>
          </div>

          {purchasable ? (
            <div className="flex gap-2 mt-6">
              <div className="flex items-center border border-[#0B0F12]">
                <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3"><Minus className="w-4 h-4" /></button>
                <span className="px-3 font-mono2" data-testid="qty-value">{qty}</span>
                <button data-testid="qty-plus" onClick={() => setQty(Math.min(99, qty + 1))} className="p-3"><Plus className="w-4 h-4" /></button>
              </div>
              <button data-testid="add-to-basket-button" onClick={() => add(p, qty, selVar)}
                className="flex-1 bg-[#0B0F12] text-[#FAFAF8] py-3.5 font-mono2 text-xs uppercase tracking-[0.2em] hover:bg-[#E54D2E] transition-colors">
                Add to Basket{disp.strength ? ` — ${disp.strength}` : ''} — {gbp(disp.price * qty)}
              </button>
            </div>
          ) : wantsInterest ? (
            <div className="mt-6 border hairline bg-white p-5">
              <p className="font-display font-bold uppercase text-lg flex items-center gap-2"><BellRing className="w-4 h-4" /> Register Interest</p>
              <p className="text-sm text-[#575B60] mt-1.5">Currently “{STATUS_LABELS[disp.status] || disp.status}{disp.status === 'available' && !disp.inStock ? ' — out of stock' : ''}”. Leave your email and we will notify you when availability changes.</p>
              <div className="flex gap-2 mt-3">
                <input data-testid="interest-email" type="email" value={interestEmail} onChange={(e) => setInterestEmail(e.target.value)}
                  placeholder="your@email.com" className="flex-1 border hairline px-4 py-2.5 text-sm focus:outline-none focus:border-[#0B0F12]" />
                <button data-testid="interest-submit" onClick={submitInterest}
                  className="bg-[#0B0F12] text-white px-5 font-mono2 text-[10px] uppercase tracking-widest hover:bg-[#E54D2E] transition-colors">Notify me</button>
              </div>
            </div>
          ) : (
            <p className="mt-6 border hairline bg-white p-4 font-mono2 text-[11px] uppercase tracking-wider text-[#575B60]" data-testid="product-unavailable">
              {STATUS_LABELS[disp.status] || disp.status} — this item cannot be purchased online.
            </p>
          )}

          <div className="flex gap-2 mt-3">
            <button data-testid="compare-button" onClick={() => { toggleCompare(p); toast.success(inCompare(p.id) ? 'Removed from comparison' : 'Added to comparison'); }}
              className={`flex-1 border py-3 font-mono2 text-[10px] uppercase tracking-[0.15em] flex items-center justify-center gap-2 transition-colors ${inCompare(p.id) ? 'bg-[#0B0F12] text-white border-[#0B0F12]' : 'border-[#D8D8D0] hover:border-[#0B0F12]'}`}>
              <GitCompareArrows className="w-4 h-4" /> {inCompare(p.id) ? 'In comparison' : 'Compare'}
            </button>
            <button data-testid="save-product-button" onClick={saveProduct}
              className="border border-[#D8D8D0] hover:border-[#0B0F12] px-4 flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.15em] transition-colors">
              <Bookmark className="w-4 h-4" /> Save
            </button>
          </div>

          <div className="mt-6 bg-[#FFF7ED] border border-[#FDE68A] p-4 flex gap-3" data-testid="research-notice">
            <ShieldAlert className="w-5 h-5 text-[#D97706] shrink-0" />
            <p className="text-[13px] leading-relaxed text-[#575B60]">
              <strong className="text-[#0B0F12]">For in-vitro laboratory research only.</strong> Not for human or veterinary use.
              VQ Health does not provide dosing, administration or protocol guidance.
            </p>
          </div>

          <p className="mt-5 text-sm text-[#575B60] leading-relaxed">{p.short_description}</p>
        </div>
      </div>

      {/* RESEARCH OVERVIEW MODULE */}
      {ro && (
        <section className="mt-14 border hairline bg-white" data-testid="research-overview">
          <div className="p-6 sm:p-8 border-b hairline">
            <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#8A8F95]">Compound Dossier</p>
            <h2 className="font-display font-black uppercase text-3xl tracking-tight mt-2">Research Overview</h2>
            <p className="text-sm text-[#575B60] mt-2 max-w-2xl">Educational research content. Established, peer-reviewed findings are clearly distinguished from investigational research.</p>
          </div>
          <div className="divide-y divide-[#E5E5DF]">
            {RO_SECTIONS.filter(([k]) => ro[k]).map(([k, label], i) => (
              <details key={k} open={i === 0} className="group" data-testid={`ro-section-${k}`}>
                <summary className="cursor-pointer px-6 sm:px-8 py-5 font-display font-bold uppercase tracking-wide text-lg list-none flex justify-between items-center hover:bg-[#FAFAF8] transition-colors">
                  {label}
                  <span className="font-mono2 text-[#8A8F95] group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                </summary>
                <div className="px-6 sm:px-8 pb-6 vq-prose max-w-3xl" dangerouslySetInnerHTML={{ __html: mdToHtml(ro[k]) }} />
              </details>
            ))}
          </div>
          {relatedArticles.length > 0 && (
            <div className="p-6 sm:p-8 border-t hairline">
              <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F95] mb-4">Related Journal Articles</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {relatedArticles.map((a) => (
                  <Link key={a.id} to={`/journal/${a.slug}`} className="border hairline p-4 hover:border-[#0B0F12] transition-colors">
                    <p className="font-mono2 text-[9px] uppercase tracking-[0.18em] text-[#8A8F95]">{a.category}</p>
                    <p className="font-display font-bold uppercase text-base leading-tight mt-1.5">{a.title}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* TABS */}
      <div className="mt-14 border-t hairline">
        <div className="flex gap-1 overflow-x-auto -mt-px">
          {TABS.map(([k, l]) => (
            <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
              className={`px-4 py-3.5 font-mono2 text-[10px] uppercase tracking-[0.15em] whitespace-nowrap border-t-2 transition-colors ${tab === k ? 'border-[#0B0F12] text-[#0B0F12]' : 'border-transparent text-[#8A8F95] hover:text-[#0B0F12]'}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="py-8 max-w-3xl">
          {tab === 'research' && (
            <div className="vq-prose">
              {p.long_description && <div dangerouslySetInnerHTML={{ __html: mdToHtml(p.long_description) }} />}
              <div dangerouslySetInnerHTML={{ __html: mdToHtml(p.research_information || '') }} />
            </div>
          )}
          {tab === 'spec' && (
            <div className="vq-prose">
              <table className="w-full text-sm">
                <tbody>
                  {[['SKU', disp.sku], ['Format', p.format], ['Strength', disp.strength || `${activeVariants.length} strengths`],
                    ['Category', cat.name], ['Composition', p.composition], ['Batch', disp.batch || 'Assigned at fulfilment']].map(([k, v]) => (
                    <tr key={k} className="border-b hairline">
                      <td className="py-2.5 font-mono2 text-[11px] uppercase tracking-wider text-[#8A8F95] w-40">{k}</td>
                      <td className="py-2.5">{v || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {p.specification && <div className="mt-4" dangerouslySetInnerHTML={{ __html: mdToHtml(p.specification) }} />}
            </div>
          )}
          {tab === 'storage' && <div className="vq-prose" dangerouslySetInnerHTML={{ __html: mdToHtml(p.storage_information || 'See compound-class guidance in the Documentation Centre.') }} />}
          {tab === 'documents' && (
            <div className="space-y-3" data-testid="product-documents">
              {selVar?.coa_document && (
                <a href={selVar.coa_document} target="_blank" rel="noreferrer" data-testid="variant-coa-download"
                  className="flex items-center gap-3 border hairline bg-white p-4 hover:border-[#0B0F12] transition-colors">
                  <FileCheck className="w-5 h-5" />
                  <div className="flex-1">
                    <p className="font-display font-bold uppercase">COA — {selVar.strength}{selVar.batch_number ? ` — Batch ${selVar.batch_number}` : ''}</p>
                  </div>
                  <span className="font-mono2 text-[10px] uppercase tracking-widest underline">PDF</span>
                </a>
              )}
              {coas.map((c) => (
                <a key={c.id} href={c.pdf_url} target="_blank" rel="noreferrer" data-testid={`coa-download-${c.id}`}
                  className="flex items-center gap-3 border hairline bg-white p-4 hover:border-[#0B0F12] transition-colors">
                  <FileCheck className="w-5 h-5" />
                  <div className="flex-1">
                    <p className="font-display font-bold uppercase">COA — Batch {c.batch}</p>
                    <p className="font-mono2 text-[10px] uppercase tracking-wider text-[#8A8F95]">{c.laboratory} · {c.test_type}</p>
                  </div>
                  <span className="font-mono2 text-[10px] uppercase tracking-widest underline">PDF</span>
                </a>
              ))}
              {p.sds_document && <a href={p.sds_document} target="_blank" rel="noreferrer" className="flex items-center gap-3 border hairline bg-white p-4 hover:border-[#0B0F12]"><FileCheck className="w-5 h-5" /><p className="font-display font-bold uppercase">Safety Data Sheet</p></a>}
              {(p.supporting_documents || []).map((d, i) => (
                <a key={i} href={d.url} target="_blank" rel="noreferrer" className="flex items-center gap-3 border hairline bg-white p-4 hover:border-[#0B0F12]"><FileCheck className="w-5 h-5" /><p className="font-display font-bold uppercase">{d.label || `Document ${i + 1}`}</p></a>
              ))}
              {!coas.length && !selVar?.coa_document && !p.sds_document && !(p.supporting_documents || []).length && (
                <p className="text-sm text-[#575B60]">No documents are currently published for this product. Certificates are only shown where a genuine batch-specific document exists — we never publish placeholder results.</p>
              )}
            </div>
          )}
          {tab === 'shipping' && (
            <div className="vq-prose">
              <p>Orders are dispatched from the UK in protective, discreet packaging suitable for laboratory materials. Tracked 48 arrives in 2–3 working days; Special Delivery arrives the next working day by 1pm. Current shipping prices and free-shipping thresholds are shown at checkout.</p>
            </div>
          )}
          {tab === 'important' && (
            <div className="vq-prose">
              <p><strong>For in-vitro laboratory research only. Not for human or veterinary use.</strong></p>
              <p>This product is a research reference material. It is not a medicine, food supplement or cosmetic. Nothing on this page — or anywhere on this site — constitutes medical advice, dosing guidance or a protocol of any kind. Purchasers must be 18 or over and handle materials only in appropriate laboratory settings.</p>
            </div>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-14">
          <h2 className="font-display font-black uppercase text-2xl sm:text-3xl tracking-tight mb-6">Related Research</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {related.map((r) => <ProductCard key={r.id} product={r} />)}
          </div>
        </section>
      )}
    </div>
  );
}
